import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Paper,
  useTheme,
  Typography,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useTranslation } from 'react-i18next';
import {
  UploadState,
  FeedbackSnackbar,
  ProgressModal,
  convertTextToSpeech,
  uploadToScreenApp,
  useIframeResizer,
} from './common';

const MAX_CHARS = 200;

interface TextToSpeechState extends UploadState {
  text: string;
  isLoading: boolean;
  audioUrl: string | null;
  audioBlob: Blob | null;
  showError: boolean;
}

const TextToSpeech: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [state, setState] = useState<TextToSpeechState>({
    text: '',
    isLoading: false,
    audioUrl: null,
    audioBlob: null,
    showError: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });

  useIframeResizer();

  const charsRemaining = MAX_CHARS - state.text.length;
  const isNearLimit = charsRemaining < 20;

  useEffect(() => {
    return () => {
      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }
    };
  }, [state.audioUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.text.trim()) return;

    setState((prev) => ({
      ...prev,
      isLoading: true,
      audioUrl: null,
      audioBlob: null,
      error: null,
    }));

    try {
      const blob = await convertTextToSpeech(state.text);
      const url = URL.createObjectURL(blob);
      setState((prev) => ({
        ...prev,
        audioUrl: url,
        audioBlob: blob,
        isLoading: false,
      }));

      // Automatically upload to ScreenApp
      uploadToScreenApp(
        blob,
        'text-to-speech.mp3',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => setState((prev) => ({ ...prev, uploadComplete: true })),
        (error) => setState((prev) => ({ ...prev, error, showError: true }))
      );
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to convert text to speech. Please try again.',
        showError: true,
        isLoading: false,
      }));
    }
  };

  const handleClear = () => {
    setState((prev) => ({
      ...prev,
      text: '',
      audioUrl: null,
      audioBlob: null,
      error: null,
    }));
  };

  const handleReset = () => {
    setState({
      text: '',
      audioUrl: null,
      audioBlob: null,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
      showError: false,
      isLoading: false,
    });
  };

  const handleSaveAgain = () => {
    if (state.audioBlob) {
      uploadToScreenApp(
        state.audioBlob,
        'text-to-speech.mp3',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => setState((prev) => ({ ...prev, uploadComplete: true })),
        (error) => setState((prev) => ({ ...prev, error, showError: true }))
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        p: { xs: 0, sm: 3 },
        position: 'relative',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: { xs: '100%', sm: 600 },
          minHeight: { xs: '100vh', sm: 'auto' },
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 0, sm: 2 },
          border: { xs: 'none', sm: `1px solid ${theme.palette.divider}` },
        }}
      >
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            value={state.text}
            onChange={(e) =>
              setState((prev) => ({ ...prev, text: e.target.value }))
            }
            placeholder={t('textToSpeech.input.placeholder')}
            variant="outlined"
            disabled={state.isLoading || state.uploading}
            inputProps={{
              maxLength: MAX_CHARS,
              'aria-label': t('textToSpeech.input.placeholder'),
            }}
            sx={{ mb: 1 }}
          />
          <Typography
            variant="caption"
            component="div"
            color={isNearLimit ? 'error' : 'text.secondary'}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              mx: 0.5,
            }}
          >
            <span>{charsRemaining} characters remaining</span>
            {state.text && !state.uploading && (
              <Tooltip title={t('textToSpeech.input.clear')}>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  aria-label={t('textToSpeech.input.clear')}
                >
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Typography>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={state.isLoading || !state.text.trim() || state.uploading}
            startIcon={<PlayArrowRoundedIcon />}
            aria-label={t('textToSpeech.controls.generate')}
          >
            {t('textToSpeech.controls.generate')}
          </Button>
        </form>

        {state.audioUrl && (
          <Box sx={{ mt: 3 }}>
            <audio controls src={state.audioUrl} style={{ width: '100%' }}>
              <track kind="captions" />
            </audio>
          </Box>
        )}

        <ProgressModal
          open={state.uploading}
          progress={state.progress}
          isComplete={state.uploadComplete}
          processingMessage={t('textToSpeech.messages.processing')}
          completeMessage={t('textToSpeech.messages.complete')}
          onReset={handleReset}
          onSaveAgain={handleSaveAgain}
        />

        <FeedbackSnackbar
          open={!!state.error && state.showError}
          message={state.error || t('textToSpeech.messages.error')}
          severity="error"
          onClose={() => setState((prev) => ({ ...prev, showError: false }))}
        />
      </Paper>
    </Box>
  );
};

export default TextToSpeech;
